import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api/client';
import { InfoPanel } from '../components/InfoPanel';
import type { ApiKeySummary, IssuedApiKey } from '../api/types';

export function ApiKeysPage() {
  const [items, setItems] = useState<ApiKeySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const list = await api.listApiKeys();
      setItems(list);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleIssue = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.issueApiKey(name);
      setIssued(result);
      setName('');
      refresh();
    } catch (err) {
      const msg =
        err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('이 API key를 회수합니다. 사용 중인 서비스가 있다면 즉시 401을 받게 됩니다.'))
      return;
    try {
      await api.revokeApiKey(id);
      refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <h2>API keys</h2>
      <InfoPanel
        what={
          <p style={{ margin: 0 }}>
            서버↔서버 자동화에 쓰는 자격 증명입니다. 발급된 API key 로 mbtiles
            업로드·삭제·메타 변경 같은 쓰기 API 를 admin 로그인 없이 호출할 수
            있습니다.
          </p>
        }
        why={
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              CI 파이프라인이나 외부 시스템이 mbtiles 를 자동 갱신하는 경우 admin
              비밀번호 대신 좁은 범위의 키를 발급해 두는 게 안전합니다.
            </li>
            <li>
              자격 유출 시 권한 확장을 막기 위해 API key 자체로는 다른 키 발급/회수와
              read-token 관리는 할 수 없습니다 (admin JWT 전용).
            </li>
            <li>
              plain key 는 발급 직후 한 번만 노출됩니다 — 그 자리에서 안전한 곳에
              저장하고, 잃어버리면 회수 후 새로 발급하세요.
            </li>
          </ul>
        }
      />
      {error && (
        <div className="banner error" data-testid="api-keys-error">
          {error}
        </div>
      )}

      {issued && (
        <div className="banner" data-testid="api-keys-issued">
          <strong>새 API key — 이 값은 다시 보여주지 않습니다.</strong>
          <pre
            style={{
              margin: '0.5rem 0',
              padding: '0.5rem',
              background: 'rgba(0,0,0,0.06)',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
            data-testid="api-keys-issued-plain"
          >
            {issued.plain}
          </pre>
          <button className="btn ghost" onClick={() => setIssued(null)} data-testid="api-keys-issued-dismiss">
            확인했습니다
          </button>
        </div>
      )}

      <div className="card">
        <h3>새 key 발급</h3>
        <form onSubmit={handleIssue} className="row">
          <input
            type="text"
            value={name}
            placeholder="이름 (예: ci-uploader)"
            required
            onChange={(e) => setName(e.target.value)}
            data-testid="api-keys-name"
          />
          <button
            className="btn"
            type="submit"
            disabled={!name || busy}
            data-testid="api-keys-submit"
          >
            {busy ? '발급 중…' : '발급'}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>발급된 keys</h3>
        {!items ? (
          <div>로딩 중…</div>
        ) : items.length === 0 ? (
          <div>발급된 key가 없습니다.</div>
        ) : (
          <table data-testid="api-keys-table">
            <thead>
              <tr>
                <th>이름</th>
                <th>prefix</th>
                <th>상태</th>
                <th>발급일</th>
                <th>마지막 사용</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id} data-testid={`api-keys-row-${k.id}`}>
                  <td>{k.name}</td>
                  <td>
                    <span className="code">{k.prefix}…</span>
                  </td>
                  <td>
                    {k.revokedAt ? (
                      <span className="badge private">회수됨</span>
                    ) : (
                      <span className="badge public">활성</span>
                    )}
                  </td>
                  <td>{new Date(k.createdAt).toLocaleString()}</td>
                  <td>
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                  </td>
                  <td>
                    {!k.revokedAt && (
                      <button
                        className="btn danger"
                        onClick={() => handleRevoke(k.id)}
                        data-testid={`api-keys-revoke-${k.id}`}
                      >
                        회수
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
